import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  ArrowLeft, 
  FileText, 
  Building, 
  Check, 
  CreditCard,
  Globe,
  Calendar,
  Loader2,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function ReportPurchase() {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('single_report');
  const [countries, setCountries] = useState([]);
  const [surveys, setSurveys] = useState([]);
  
  const [form, setForm] = useState({
    organization_type: '',
    organization_name: '',
    email: '',
    country_code: 'NG',
    survey_id: searchParams.get('survey') || '',
    regions: [],
    sectors: [],
    report_language: language,
    include_branding: false,
    branding_logo_url: '',
    period_start: `${new Date().getFullYear()}-01`,
    period_end: `${new Date().getFullYear()}-12`
  });

  useEffect(() => {
    fetchPricing();
    fetchCountries();
    fetchSurveys();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await fetch(`${API}/reports/pricing`);
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API}/countries`);
      if (response.ok) {
        const data = await response.json();
        setCountries(data);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchSurveys = async () => {
    try {
      const response = await fetch(`${API}/surveys`);
      if (response.ok) {
        const data = await response.json();
        setSurveys(data);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  const handlePurchase = async () => {
    if (!form.organization_type || !form.organization_name || !form.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!form.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API}/reports/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_type: selectedPackage,
          ...form
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to process purchase');
      }
    } catch (error) {
      console.error('Error processing purchase:', error);
      toast.error('Failed to process purchase');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const selectedPricing = pricing?.pricing?.[selectedPackage];

  return (
    <div className="min-h-screen bg-background" data-testid="report-purchase">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('nav_home', 'Home')}
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
                  <span className="text-white font-bold text-xs">CV</span>
                </div>
                <span className="font-semibold">Citizen Voice Africa</span>
              </div>
            </div>
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-8">
          <Badge variant="secondary" className="mb-4">
            <Building className="h-3 w-3 mr-1" />
            {t('report_paywall_title', 'Institutional Report Access')}
          </Badge>
          <h1 className="text-3xl font-bold mb-2">
            {language === 'fr' ? 'Rapports Institutionnels' : 'Institutional Reports'}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t('report_paywall_desc', 'Access detailed analytics and custom reports for your organization.')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Pricing Cards */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-lg font-semibold mb-4">
              {language === 'fr' ? 'Choisissez Votre Forfait' : 'Choose Your Package'}
            </h2>
            
            <div className="grid sm:grid-cols-3 gap-4">
              {pricing?.pricing && Object.entries(pricing.pricing).map(([key, pkg]) => (
                <Card 
                  key={key}
                  className={`cursor-pointer transition-all ${
                    selectedPackage === key 
                      ? 'border-primary ring-2 ring-primary/20' 
                      : 'hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedPackage(key)}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      {key === 'single_report' ? (language === 'fr' ? 'Rapport Unique' : 'Single Report') :
                       key === 'monthly_subscription' ? (language === 'fr' ? 'Mensuel' : 'Monthly') :
                       (language === 'fr' ? 'Annuel' : 'Annual')}
                      {selectedPackage === key && (
                        <Check className="h-5 w-5 text-primary" />
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <span className="text-3xl font-bold">${pkg.price}</span>
                      <span className="text-muted-foreground text-sm">
                        {key === 'monthly_subscription' ? '/mo' : 
                         key === 'annual_subscription' ? '/yr' : ''}
                      </span>
                    </div>
                    <ul className="space-y-2 text-sm">
                      {pkg.includes.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Organization Details */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">
                  {language === 'fr' ? 'Détails de l\'Organisation' : 'Organization Details'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{language === 'fr' ? 'Type d\'Organisation' : 'Organization Type'} *</Label>
                  <Select
                    value={form.organization_type}
                    onValueChange={(value) => setForm({ ...form, organization_type: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder={language === 'fr' ? 'Sélectionner' : 'Select type'} />
                    </SelectTrigger>
                    <SelectContent>
                      {pricing?.organization_types?.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {language === 'fr' ? org.name_fr : org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{language === 'fr' ? 'Nom de l\'Organisation' : 'Organization Name'} *</Label>
                    <Input
                      value={form.organization_name}
                      onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                      placeholder="e.g., Africa Research Institute"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{language === 'fr' ? 'Email' : 'Email Address'} *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="contact@organization.org"
                      className="mt-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Report Customization */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {t('customize_report', 'Customize Report')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{language === 'fr' ? 'Pays' : 'Country'}</Label>
                    <Select
                      value={form.country_code}
                      onValueChange={(value) => setForm({ ...form, country_code: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {language === 'fr' ? c.name_fr : c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{language === 'fr' ? 'Enquête Spécifique' : 'Specific Survey'}</Label>
                    <Select
                      value={form.survey_id || "all"}
                      onValueChange={(value) => setForm({ ...form, survey_id: value === "all" ? "" : value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder={language === 'fr' ? 'Toutes les enquêtes' : 'All surveys'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          {language === 'fr' ? 'Toutes les enquêtes' : 'All surveys'}
                        </SelectItem>
                        {surveys.map((s) => (
                          <SelectItem key={s.survey_id} value={s.survey_id}>
                            {s.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>{t('select_language', 'Report Language')}</Label>
                  <Select
                    value={form.report_language}
                    onValueChange={(value) => setForm({ ...form, report_language: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="yo">Yorùbá</SelectItem>
                      <SelectItem value="ha">Hausa</SelectItem>
                      <SelectItem value="ig">Igbo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>{t('reporting_period', 'Reporting Period')}</Label>
                  <div className="grid grid-cols-2 gap-4 mt-1">
                    <div>
                      <Label className="text-xs text-muted-foreground">Start</Label>
                      <Input
                        type="month"
                        value={form.period_start}
                        onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">End</Label>
                      <Input
                        type="month"
                        value={form.period_end}
                        onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="branding"
                    checked={form.include_branding}
                    onCheckedChange={(checked) => setForm({ ...form, include_branding: checked })}
                  />
                  <Label htmlFor="branding" className="text-sm font-normal">
                    {t('add_branding', 'Add Organization Branding (logo on report)')}
                  </Label>
                </div>

                {form.include_branding && (
                  <div>
                    <Label>{language === 'fr' ? 'URL du Logo' : 'Logo URL'}</Label>
                    <Input
                      value={form.branding_logo_url}
                      onChange={(e) => setForm({ ...form, branding_logo_url: e.target.value })}
                      placeholder="https://example.com/logo.png"
                      className="mt-1"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary & Checkout */}
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>{language === 'fr' ? 'Résumé de la Commande' : 'Order Summary'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'fr' ? 'Forfait' : 'Package'}</span>
                  <span className="font-medium">
                    {selectedPackage === 'single_report' ? (language === 'fr' ? 'Rapport Unique' : 'Single Report') :
                     selectedPackage === 'monthly_subscription' ? (language === 'fr' ? 'Mensuel' : 'Monthly') :
                     (language === 'fr' ? 'Annuel' : 'Annual')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'fr' ? 'Pays' : 'Country'}</span>
                  <span>{countries.find(c => c.code === form.country_code)?.name || form.country_code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'fr' ? 'Langue' : 'Language'}</span>
                  <span className="capitalize">{form.report_language}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{language === 'fr' ? 'Période' : 'Period'}</span>
                  <span>{form.period_start} to {form.period_end}</span>
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span>${selectedPricing?.price || 0} USD</span>
                  </div>
                </div>

                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={handlePurchase}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {language === 'fr' ? 'Traitement...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" />
                      {t('purchase_report', 'Purchase Report')}
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <Shield className="h-3 w-3" />
                  {language === 'fr' ? 'Paiement sécurisé via Stripe' : 'Secure payment via Stripe'}
                </div>
              </CardContent>
            </Card>

            {/* Disclaimer */}
            <Card className="mt-4">
              <CardContent className="py-4">
                <p className="text-xs text-muted-foreground">
                  {t('disclaimer', 'This platform collects citizen sentiment data, not election results. All data is aggregated and anonymized.')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
